import React, { useState, useCallback, useEffect } from 'react';
import { Form, Input, InputNumber, Button, Table, Tag, Space, Row, Col, Modal, Select, Popconfirm, message, Switch, Divider, Card, List, Empty, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined, CopyOutlined, UserOutlined, SettingOutlined } from '@ant-design/icons';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import apiClient from 'services/apiClient';
import { useMyContext } from 'Context/MyContextProvider';
import { PermissionData } from '../Role/RolePermission';
import { fetchPlans, handleFeatureChange } from './PricingUtils';
import { ErrorExtractor } from '../CustomUtils.js/utils';
const { Option } = Select;

const PlanManagement = ({ plans, setPlans, features, setFeatures, loading, roles }) => {
    const { api, authToken } = useMyContext();
    const [editingPlanId, setEditingPlanId] = useState(null);
    const [isPlanModalVisible, setIsPlanModalVisible] = useState(false);
    const [isAddFeatureModalVisible, setIsAddFeatureModalVisible] = useState(false);
    const [isPlanConfigModalVisible, setIsPlanConfigModalVisible] = useState(false);
    const [planForm] = Form.useForm();
    const [featureForm] = Form.useForm();
    const [planConfigForm] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [permissions, setPermissions] = useState([]);


    useEffect(() => {
        fetchPlans(api, authToken, setPlans);
    }, []);

    // Common function to handle plan submission (both add and update)
    const handlePlanSubmit = async (values, isEditing = false) => {
        setSubmitting(true);

        try {
            const apiPayload = {
                title: values.title,
                monthly_price: values.monthlyPrice || 0,
                yearly_price: values.yearlyPrice || 0,
                custom_price: values.customPrice || null,
                role_id: values.role_id || null,
                button_text: values.buttonText,
                features: values.features || [],
                recommended: values.recommended || false,
                active: values.active || true
            };
            let response;

            if (isEditing) {
                response = await apiClient.put(`${api}plans/${editingPlanId}`, apiPayload, {
                    headers: { 'Authorization': `Bearer ${authToken}` }
                });
                message.success('Plan updated successfully');
            } else {
                response = await apiClient.post(`${api}plans`, apiPayload, {
                    headers: { 'Authorization': `Bearer ${authToken}` }
                });
                message.success('Plan added successfully');
            }

            if (response.data.success) {
                await fetchPlans(api, setPlans); // Refresh plans after successful operation
            }

            setIsPlanModalVisible(false);
            setEditingPlanId(null);
            planForm.resetFields();
        } catch (error) {
            console.error(`Error ${isEditing ? 'updating' : 'adding'} plan:`, error);
            message.error(`Failed to ${isEditing ? 'update' : 'add'} plan. Please try again.`);
        } finally {
            setSubmitting(false);
        }
    };

    // Plan CRUD operations
    const addPlan = (values) => {
        handlePlanSubmit(values, false);
    };

    const updatePlan = (values) => {
        handlePlanSubmit(values, true);
    };

    const deletePlan = async (id) => {
        try {
            const response = await apiClient.delete(`${api}plans/${id}`, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });

            if (response.data.success) {
                message.success('Plan deleted successfully');
                await fetchPlans(); // Refresh plans after deletion
            }
        } catch (error) {
            console.error('Error deleting plan:', error);
            message.error('Failed to delete plan. Please try again.');
        }
    };

    const editPlan = (record) => {
        setEditingPlanId(record.id);
        planForm.setFieldsValue({
            title: record.title,
            monthlyPrice: record.monthly_price,
            yearlyPrice: record.yearly_price,
            role_id: record.role_id,
            customPrice: record.custom_price,
            buttonText: record.button_text,
            features: record.features,
            recommended: record.recommended === 1,
            active: record.active === 1
        });
        setIsPlanModalVisible(true);
    };

    // Quick add feature function
    const quickAddFeature = async (values) => {
        const newFeature = {
            id: features.length ? Math.max(...features.map(f => f.id)) + 1 : 1,
            name: values.name,
            description: values.description || ''
        };
        await handleFeatureChange(api, authToken, newFeature, values, setFeatures, features)
        setFeatures([...features, newFeature]);

        // Get the current selected features
        const currentFeatures = planForm.getFieldValue('features') || [];
        // Add the new feature to the selection
        planForm.setFieldsValue({
            features: [...currentFeatures, newFeature.id]
        });

        //message.success('Feature added successfully');
        setIsAddFeatureModalVisible(false);
        featureForm.resetFields();
    };

    // Plan columns for table
    const planColumns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: 'Monthly Price',
            key: 'monthly_price',
            render: (_, record) => (
                record.custom_price ? record.custom_price : `$${record.monthly_price}`
            ),
        },
        {
            title: 'Yearly Price',
            key: 'yearly_price',
            render: (_, record) => (
                record.custom_price ? record.custom_price : `$${record.yearly_price}`
            ),
        },
        {
            title: 'Button Text',
            dataIndex: 'button_text',
            key: 'buttonText',
        },
        {
            title: 'Features',
            key: 'features',
            render: (_, record) => (
                <span>
                    {record.features.length} features assigned
                    <Popconfirm
                        title={
                            <div style={{ maxWidth: '300px', maxHeight: '200px', overflow: 'auto' }}>
                                <ul style={{ paddingLeft: '20px', margin: 0 }}>
                                    {features
                                        .filter(feature => record.features.includes(feature.id))
                                        .map(feature => (
                                            <li key={feature.id}>{feature.name}</li>
                                        ))
                                    }
                                </ul>
                            </div>
                        }
                        icon={null}
                        okText="Close"
                        cancelButtonProps={{ style: { display: 'none' } }}
                    >
                        <Button type="link" size="small">View</Button>
                    </Popconfirm>
                </span>
            ),
        },
        {
            title: 'Recommended',
            key: 'recommended',
            render: (_, record) => (
                record.recommended === 1 ? <Tag color="green">Yes</Tag> : <Tag color="default">No</Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space size="small">
                    <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => editPlan(record)}
                    />
                    <Popconfirm
                        title="Are you sure you want to delete this plan?"
                        onConfirm={() => deletePlan(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const FetchRolePermisson = useCallback(async (id) => {
        setPermissions([]);
        if (!id) return; // If no role is selected, do not fetch permissions
        setIsLoading(true);
        const data = await PermissionData(api, authToken, id);
        const all = data.AllPermission;
        const exist = data.exist;
        const roleFeatures = all.filter(feature => exist.includes(feature.id));
        const permissions = roleFeatures.map(feature => feature.name);
        setPermissions(permissions);
        setIsLoading(false);
    }, [api, authToken]);
    
    const fetchPlanConfig = useCallback(async (planId) => {
        if (!planId) return;
        // resetPlanConfigForm();
        //planConfigForm.resetFields();
        setSubmitting(true);
        try {
            const response = await apiClient.get(`${api}plans/${planId}/config`, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });
            
            if (response.data && response.data.data) {
                // Update the form with the retrieved values
                planConfigForm.setFieldsValue({
                    supportPlan: planId,
                    supportAgentCount: response.data.data.support_agent || 0,
                    contactGroupCount: response.data.data.contact_group || 0,
                    mediaStorage: response.data.data.media_storage || 0
                });
                //message.success('Plan configuration loaded');
            } else {
                // Reset the values if no data is found
                planConfigForm.setFieldsValue({
                    supportPlan: planId,
                    supportAgentCount: 0,
                    contactGroupCount: 0,
                    mediaStorage: 0
                });
            }
        } catch (error) {
            console.error('Error fetching plan config:', error);
            message.warning('Could not retrieve existing configuration');
            // Reset the values on error
            planConfigForm.setFieldsValue({
                supportPlan: planId,
                supportAgentCount: 0,
                contactGroupCount: 0,
                mediaStorage: 0
            });
        } finally {
            setSubmitting(false);
        }
    }, [api, authToken, planConfigForm]);

    const HanlePlanConfig = useCallback(async (values) => {
        setSubmitting(true);
        try {
            const response = await apiClient.post(`${api}plans/${values?.supportPlan}/config`, {
                support_agent: values.supportAgentCount,
                contact_group: values.contactGroupCount,
                media_storage: values.mediaStorage,
                plan_id: values.supportPlan,
            }, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });
            if (response.data.status) {
                message.success('Plan configuration updated successfully');
            }
            setIsPlanConfigModalVisible(false);
        } catch (error) {
            const err = ErrorExtractor(error);
            message.error(err);
        } finally {
            setSubmitting(false);
        }
    } , [api, authToken]);
    return (
        <>
            <div className='d-flex gap-2 mb-3'>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        planForm.resetFields();
                        setEditingPlanId(null);
                        setIsPlanModalVisible(true);
                    }}
                >
                    Add New Plan
                </Button>
                <Button
                    type="primary"
                    icon={<SettingOutlined />}
                    onClick={() => setIsPlanConfigModalVisible(true)}
                >
                    Manage Plan Config
                </Button>
               
            </div>
            <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                <Table
                    columns={planColumns}
                    dataSource={plans}
                    rowKey="id"
                    bordered
                    size="middle"
                    pagination={false}
                    loading={loading || submitting}
                />
            </div>

            {/* Plan Form Modal */}
            <Modal
                title={editingPlanId ? "Edit Plan" : "Add New Plans"}
                open={isPlanModalVisible}
                onCancel={() => {
                    setIsPlanModalVisible(false);
                    setEditingPlanId(null);
                    planForm.resetFields();
                    setPermissions([]);
                }}
                footer={null}
                destroyOnClose
                width={900}
                confirmLoading={submitting}
            >
                <Row gutter={ROW_GUTTER}>
                    <Col span={16}>
                        <Form
                            form={planForm}
                            layout="vertical"
                            onFinish={editingPlanId ? updatePlan : addPlan}
                        >
                            <Row gutter={ROW_GUTTER}>
                                <Col span={12}>
                                    <Form.Item
                                        name="title"
                                        label="Plan Title"
                                        rules={[{ required: true, message: 'Please enter the plan title' }]}
                                    >
                                        <Input placeholder="e.g., Basic, Premium, Enterprise" />
                                    </Form.Item>
                                </Col>
                                {/* add select for roles */}
                                <Col span={12}>
                                    <Form.Item
                                        name="role_id"
                                        label="Assign Role"
                                        rules={[{ required: true, message: 'Please select at least one role' }]}
                                    >
                                        <Select
                                            placeholder="Select roles for this plan"
                                            style={{ width: '100%' }}
                                            optionFilterProp="children"
                                            onChange={(value) => { FetchRolePermisson(value) }}
                                        >
                                            {roles.map(role => (
                                                <Option key={role.id} value={role.id}>{role.name}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="buttonText"
                                        label="Button Text"
                                        rules={[{ required: true, message: 'Please enter button text' }]}
                                    >
                                        <Input placeholder="e.g., Start Now, Upgrade" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="monthlyPrice"
                                        label="Monthly Price ($)"
                                        rules={[
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    if (getFieldValue('customPrice') || value !== undefined) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Please enter price or use custom pricing'));
                                                },
                                            }),
                                        ]}
                                    >
                                        <InputNumber
                                            placeholder="9.99"
                                            style={{ width: '100%' }}
                                            min={0}
                                            precision={2}
                                            step={0.01}
                                            disabled={planForm.getFieldValue('customPrice')}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="yearlyPrice"
                                        label="Yearly Price ($)"
                                        rules={[
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    if (getFieldValue('customPrice') || value !== undefined) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Please enter price or use custom pricing'));
                                                },
                                            }),
                                        ]}
                                    >
                                        <InputNumber
                                            placeholder="99.99"
                                            style={{ width: '100%' }}
                                            min={0}
                                            precision={2}
                                            step={0.01}
                                            disabled={planForm.getFieldValue('customPrice')}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="customPrice"
                                        label="Custom Price Text (overrides price fields)"
                                    >
                                        <Input
                                            placeholder="e.g., Custom, Contact Sales"
                                            onChange={() => {
                                                // If custom price is set, clear the numeric prices
                                                if (planForm.getFieldValue('customPrice')) {
                                                    planForm.setFieldsValue({
                                                        monthlyPrice: null,
                                                        yearlyPrice: null
                                                    });
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item
                                        name="features"
                                        label="Included Features"
                                        rules={[{ required: true, message: 'Please select at least one feature' }]}
                                        extra={
                                            <div style={{ marginTop: '8px' }}>
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    icon={<PlusOutlined />}
                                                    onClick={() => setIsAddFeatureModalVisible(true)}
                                                    style={{ padding: 0 }}
                                                >
                                                    Add new feature
                                                </Button>
                                            </div>
                                        }
                                    >
                                        <Select
                                            mode="multiple"
                                            placeholder="Select features included in this plan"
                                            style={{ width: '100%' }}
                                            optionFilterProp="children"
                                            dropdownRender={menu => (
                                                <div>
                                                    {menu}
                                                    <Divider style={{ margin: '4px 0' }} />
                                                    <div style={{ display: 'flex', flexWrap: 'nowrap', padding: 8 }}>
                                                        <Button
                                                            type="link"
                                                            icon={<PlusOutlined />}
                                                            onClick={() => setIsAddFeatureModalVisible(true)}
                                                            style={{ width: '100%', textAlign: 'left' }}
                                                        >
                                                            Add new feature
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        >
                                            {features.map(feature => (
                                                <Option key={feature.id} value={feature.id}>{feature.name}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="recommended"
                                        label="Recommended Plan"
                                        valuePropName="checked"
                                    >
                                        <Switch checkedChildren="Yes" unCheckedChildren="No" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="active"
                                        label="Active Plan"
                                        valuePropName="checked"
                                    >
                                        <Switch checkedChildren="Yes" unCheckedChildren="No" defaultChecked />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item>
                                        <Row gutter={8} justify="end">
                                            <Col>
                                                <Button
                                                    onClick={() => {
                                                        setIsPlanModalVisible(false);
                                                        setEditingPlanId(null);
                                                        planForm.resetFields();
                                                    }}
                                                    disabled={submitting}
                                                >
                                                    Cancel
                                                </Button>
                                            </Col>
                                            <Col>
                                                <Button
                                                    type="primary"
                                                    htmlType="submit"
                                                    icon={<SaveOutlined />}
                                                    loading={submitting}
                                                >
                                                    {editingPlanId ? "Update" : "Save"}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </Col>
                    <Col span={8}>
                        <Card title="Permission List" size="small" style={{ borderRadius: '8px' }}>
                            <List
                                size="small"
                                style={{ minHeight: '300px', maxHeight: '500px', overflowY: 'auto', }}
                                dataSource={permissions}
                                renderItem={item => (
                                    <List.Item style={{ padding: '4px 0', fontSize: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>{item}</span>
                                        <Typography.Text copyable={{ text: item, icon: <CopyOutlined /> }} style={{ marginLeft: 'auto' }} />
                                    </List.Item>
                                )}
                                locale={<Empty />}
                            />
                        </Card>
                    </Col>
                </Row>
            </Modal>

            {/* Quick Add Feature Modal */}
            <Modal
                title="Add New Feature"
                open={isAddFeatureModalVisible}
                onCancel={() => {
                    setIsAddFeatureModalVisible(false);
                    featureForm.resetFields();
                }}
                footer={null}
                destroyOnClose
            >
                <Form
                    form={featureForm}
                    layout="vertical"
                    onFinish={quickAddFeature}
                >
                    <Form.Item
                        name="name"
                        label="Feature Name"
                        rules={[{ required: true, message: 'Please enter the feature name' }]}
                    >
                        <Input placeholder="e.g., API Access" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <Input.TextArea placeholder="Describe what this feature provides" rows={3} />
                    </Form.Item>

                    <Form.Item>
                        <Row gutter={8} justify="end">
                            <Col>
                                <Button
                                    onClick={() => {
                                        setIsAddFeatureModalVisible(false);
                                        featureForm.resetFields();
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Col>
                            <Col>
                                <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                                    Add & Select
                                </Button>
                            </Col>
                        </Row>
                    </Form.Item>
                </Form>
            </Modal>


            {/* Plan Configuration Modal */}
            <Modal
                title="Plan Configuration"
                open={isPlanConfigModalVisible}
                onCancel={() => {
                    setIsPlanConfigModalVisible(false);
                    planConfigForm.resetFields();
                }}
                footer={null}
                destroyOnClose
                confirmLoading={submitting}
            >
                <Form
                    form={planConfigForm}
                    name="customized_form_controls"
                    layout="vertical"
                    onFinish={HanlePlanConfig}
                >
                    {/* Use real plans data in select dropdown */}
                    <Form.Item 
                        label={'Select Plan'} 
                        name={'supportPlan'}
                        rules={[{ required: true, message: 'Please select a plan' }]}
                    >
                        <Select
                            placeholder="Select a plan"
                            style={{ width: '100%' }}
                            options={plans.map(plan => ({ 
                                value: plan.id.toString(), 
                                label: plan.title 
                            }))}
                            onChange={(value) => value && fetchPlanConfig(value)}
                        />
                    </Form.Item>

                    <Form.Item 
                        label={'Support Agent Count'} 
                        name={'supportAgentCount'}
                        rules={[{ required: true, message: 'Please enter support agent count' }]}
                    >
                        <InputNumber
                            min={0}
                            max={10}
                            style={{ width: '100%' }}
                            prefix={<UserOutlined className="site-form-item-icon" />}
                            placeholder='Support Agent Count'
                        />
                    </Form.Item>
                    
                    <Form.Item 
                        label={'Contact Group Count'} 
                        name={'contactGroupCount'}
                        rules={[{ required: true, message: 'Please enter contact group count' }]}
                    >
                        <InputNumber
                            min={0}
                            max={20}
                            style={{ width: '100%' }}
                            placeholder='Contact Group Count'
                        />
                    </Form.Item>
                    
                    <Form.Item 
                        label={'Media Storage'} 
                        name={'mediaStorage'}
                        tooltip="Media storage size in MB"
                        rules={[{ required: true, message: 'Please enter media storage size' }]}
                    >
                        <InputNumber
                            min={0}
                            max={10000}
                            style={{ width: '100%' }}
                            placeholder='Media Storage in MB'
                            addonAfter="MB"
                        />
                    </Form.Item>
                    
                    <Form.Item>
                        <Button 
                            type="primary" 
                            htmlType="submit" 
                            className='float-right'
                            loading={submitting}
                        >
                            Save Configuration
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default PlanManagement;